import React, { useCallback, useEffect, useMemo } from 'react';
import { useGoodsDiscounts } from '../../queries/useGoodsDiscounts';
import {
  Colors,
  Flex,
  IconChevronDown,
  IconChevronUp,
  IconTimer,
  Typography,
} from '@banhala/ui-kit';
import {
  getRemainTime,
  isNil,
  isNotNil,
  localizeNumber,
  localStorageBridge,
} from 'shared/src/utils';
import { DiscountInfo } from './components/DiscountInfo';
import type { GoodsDiscountGroupItem } from 'shared/src/api/goodsService';
import { useApplyDiscounts } from './queries/useApplyDiscounts';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';

const KEY_IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL =
  'IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL';
const ExpectedPurchasePriceContent: React.FC = () => {
  const { data: discounts } = useGoodsDiscounts();
  const logEvent = useLogGoodsAnalyticsEvent();
  const [expanded, setExpanded] = React.useState(true);
  const [checked, setChecked] = React.useState<GoodsDiscountGroupItem[]>(
    discounts!.discount_groups.map(item => item.items).flat(1),
  );
  const { data: applyDiscounts } = useApplyDiscounts({
    checked,
    groups: discounts?.discount_groups ?? [],
  });

  const salePrice = useMemo<{ price: number; rate: number }>(() => {
    const discountPrice = checked
      .filter(item => item.key === 'ABLY_PAY' || item.key === 'emoney')
      .reduce((val, cur) => val + cur.amount, 0);

    const price =
      (applyDiscounts?.sale_price ?? discounts?.sale_price ?? 0) -
      discountPrice;

    const displayConsumer = discounts?.display_consumer ?? 0;
    const rate =
      displayConsumer === 0
        ? 0
        : Math.floor(
            Math.min(Math.max((1 - price / displayConsumer) * 100, 0), 100),
          );
    return {
      price,
      rate: rate,
    };
  }, [applyDiscounts?.sale_price, checked, discounts]);

  useEffect(() => {
    const getValue = async () => {
      const result = await localStorageBridge.getBoolean(
        KEY_IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL,
      );
      setExpanded(result ?? true);
    };

    getValue();
  }, []);

  const onPressToggleExpandedStatus = useCallback(() => {
    logEvent({
      name: 'CLICK_TOGGLE',
      analytics: {
        TOGGLE_TYPE: '예상구매가',
        IS_ON: !expanded ? '1' : '0',
      },
    });
    localStorageBridge.putBoolean(
      KEY_IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL,
      !expanded,
    );
    setExpanded(prev => !prev);
  }, [expanded, logEvent]);

  if (isNil(discounts)) return null;

  if (discounts.discount_groups.length === 0) return null;

  return (
    <Flex>
      <Flex padding={{ left: 16, right: 8, vertical: 8 }}>
        <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
          <Typography type={'subtitle2'} color={'content_secondary'}>
            나의 예상 구매가
          </Typography>
          <Flex.Row
            padding={{ vertical: 8, horizontal: 8 }}
            gap={4}
            alignItems={'center'}
            onPress={onPressToggleExpandedStatus}
          >
            <Flex>
              {isNotNil(discounts.top_nudging) && (
                <Flex.Row
                  gap={2}
                  alignItems={'center'}
                  justifyContent={'flex-end'}
                >
                  <Typography color={'content_pink_primary'} type={'subtitle5'}>
                    {discounts.top_nudging.title}
                  </Typography>

                  {getRemainTime(discounts.top_nudging.finished_at) !==
                    null && (
                    <Flex.Row
                      backgroundColor={'pink5'}
                      padding={{ horizontal: 4, vertical: 2 }}
                      borderRadius={'xsmall'}
                      alignItems={'center'}
                      justifyContent={'center'}
                    >
                      <IconTimer size={12} fill={Colors.content_pink_primary} />
                      <Typography
                        type={'subtitle6'}
                        color={'content_pink_primary'}
                      >
                        {getRemainTime(discounts.top_nudging.finished_at)} 남음
                      </Typography>
                    </Flex.Row>
                  )}
                </Flex.Row>
              )}
              <Flex.Row gap={4}>
                {salePrice.rate > 0 && (
                  <Typography type={'subtitle1'} color={'content_pink_primary'}>
                    {salePrice.rate}%
                  </Typography>
                )}
                <Typography type={'subtitle1'}>
                  {localizeNumber(salePrice.price)}원
                </Typography>
              </Flex.Row>
            </Flex>
            <Flex margin={{ left: 4 }}>
              {expanded ? (
                <IconChevronUp size={16} fill={Colors['content_primary']} />
              ) : (
                <IconChevronDown size={16} fill={Colors['content_primary']} />
              )}
            </Flex>
          </Flex.Row>
        </Flex.Row>
      </Flex>
      <Flex padding={{ horizontal: 16, bottom: 16 }}>
        <DiscountInfo
          expanded={expanded}
          checked={checked}
          onChangeChecked={setChecked}
          salePrice={salePrice.price}
        />
      </Flex>
    </Flex>
  );
};

export const ExpectedPurchasePrice = React.memo(() => {
  const { data: discounts } = useGoodsDiscounts();

  if (isNil(discounts)) {
    return null;
  }
  return <ExpectedPurchasePriceContent />;
});
